<template>
  <b-modal
    id="modal-sql"
    :visible="show"
    @hidden="$emit('update:show', false)"
    title="SQL"
    centered
    size="xl"
    hide-footer
    scrollable
  >
    <b-form-group label="">
      <b-form-textarea v-model="sqlEditor" rows="10" max-rows="20" />

      <div class="mt-3">
        <b-button :disabled="!canExecute" @click="executar">
          Executar
          <b-spinner
            small
            label="Small Spinner"
            variant="white"
            v-if="loading"
          ></b-spinner>
        </b-button>
      </div>
    </b-form-group>

    <b-alert v-if="sqlBlockedReason" variant="warning" show class="mt-2">
      {{ sqlBlockedReason }}
    </b-alert>

    <b-alert v-if="error" variant="danger" show class="mt-2">
      {{ error }}
    </b-alert>

    <div class="mt-3" v-if="resultado && resultado.length">
      <h6 class="mb-2">Resultado: {{ resultado.length }} registro(s)</h6>

      <div class="table-responsive table-results">
        <table class="table table-sm table-striped table-hover">
          <thead>
            <tr>
              <th v-for="col in columns" :key="col">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in resultado" :key="idx">
              <td v-for="col in columns" :key="col">
                {{ row[col] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <b-alert
      v-else-if="resultado && !resultado.length"
      variant="info"
      show
      class="mt-3"
    >
      Nenhum registro encontrado.
    </b-alert>
  </b-modal>
</template>

<script>
import { queries } from './querys'

export default {
  name: 'SqlModal',
  props: {
    show: { type: Boolean, default: false },
    activeKey: { type: String, default: '' },
    cidadao: { type: Object, default: () => null },
    cnsList: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      resultado: null,
      error: '',
      sqlEditor: '',
    }
  },
  computed: {
    currentQuery() {
      return queries.find((q) => q.activeKey === this.activeKey) || null
    },

    params() {
      if (!this.currentQuery) return {}

      const ctx = {
        cidadao: this.cidadao,
        cnsList: this.cnsList,
      }

      const mapped =
        typeof this.currentQuery.map === 'function'
          ? this.currentQuery.map(ctx)
          : {}

      const picked = {}
      ;(this.currentQuery.params || []).forEach((p) => {
        picked[p] = mapped[p] ?? null
      })

      return picked
    },

    sqlPreview() {
      if (!this.currentQuery) return ''

      let sql = this.currentQuery.sql

      Object.entries(this.params).forEach(([k, v]) => {
        const re = new RegExp(`:${k}\\b`, 'g')
        sql = sql.replace(re, this.formatSqlValue(v))
      })

      return sql
    },

    columns() {
      if (!this.resultado || !this.resultado.length) return []
      return Object.keys(this.resultado[0])
    },

    canExecute() {
      return !this.loading && !!this.sqlEditor && !this.sqlBlockedReason
    },

    sqlBlockedReason() {
      return this.validateSqlClient(this.sqlEditor)
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.resultado = null
        this.error = ''
        this.sqlEditor = this.sqlPreview
      }
    },
    activeKey() {
      this.resultado = null
      this.error = ''
      this.sqlEditor = this.sqlPreview
    },
  },
  methods: {
    /**
     * Formata um valor para uso em SQL.
     * @param {*} val Valor a ser formatado
     * @returns {String} Valor formatado para SQL
     */
    formatSqlValue(val) {
      if (val === null || val === undefined || val === '') return 'NULL'

      // ARRAY -> vira lista pro IN (...)
      if (Array.isArray(val)) {
        return this.formatSqlList(val)
      }

      if (typeof val === 'boolean') return val ? 'TRUE' : 'FALSE'

      if (val instanceof Date) {
        const iso = val.toISOString().slice(0, 19).replace('T', ' ')
        return `'${iso}'`
      }

      const safe = String(val).replace(/'/g, "''")
      return `'${safe}'`
    },

    /**
     * Formata uma lista de valores para uso em cláusulas IN.
     * @param {Array} values Lista de valores
     * @returns {String} Lista formatada para SQL
     */
    formatSqlList(values) {
      const arr = Array.isArray(values) ? values : []
      const cleaned = arr
        .map((v) => String(v ?? '').trim())
        .filter((v) => v !== '')

      if (!cleaned.length) return '(NULL)' // evita SQL inválido: IN ()

      const quoted = cleaned.map((v) => `'${v.replace(/'/g, "''")}'`).join(', ')

      return `(${quoted})`
    },

    /**
     * Remove comentários e normaliza o conteúdo do SQL.
     * @param {String} sql SQL informado pelo usuário
     * @returns {String} SQL normalizado
     */
    normalizeSql(sql) {
      if (!sql) return ''

      // remove comentários simples (não é perfeito, mas ajuda bastante)
      let s = String(sql)

      // remove bloco /* ... */
      s = s.replace(/\/\*[\s\S]*?\*\//g, ' ')

      // remove linha -- ...
      s = s.replace(/--.*$/gm, ' ')

      return s.trim()
    },

    /**
     * Verifica se o SQL contém um token não permitido.
     * @param {String} sql SQL a ser validado
     * @param {String} token Token bloqueado
     * @returns {Boolean}
     */
    hasForbiddenToken(sql, token) {
      // token pode ser palavra simples ou expressão tipo "information_schema"
      const re = new RegExp(`\\b${token.replace(/\./g, '\\.')}\\b`, 'i')
      return re.test(sql)
    },

    /**
     * Valida o SQL informado antes da execução.
     * @param {String} sql SQL a ser validado
     * @returns {String} Mensagem de erro ou vazio quando válido
     */
    validateSqlClient(sql) {
      const raw = this.normalizeSql(sql)

      if (!raw) return 'SQL é obrigatório.'

      // Começo permitido
      const isSelectOrWith = /^\s*(SELECT|WITH)\b/i.test(raw)

      const isExplainSelect =
        /^\s*EXPLAIN\s*\(\s*ANALYZE\s*,\s*BUFFERS\s*,\s*VERBOSE\s*,\s*SETTINGS\s*,\s*SUMMARY\s*\)\s*(SELECT|WITH)\b/i.test(
          raw
        )

      if (!isSelectOrWith && !isExplainSelect) {
        return 'Somente SELECT, WITH ... SELECT ou EXPLAIN de SELECT é permitido.'
      }

      // bloquear ; no meio (permite ; só no final)
      const withoutTrailingSemicolons = raw.replace(/[;\s]+$/g, '')

      if (withoutTrailingSemicolons.includes(';')) {
        return 'Não é permitido usar ";" no meio do SQL.'
      }

      const blockedKeywords = [
        'INSERT',
        'UPDATE',
        'DELETE',
        'TRUNCATE',
        'REPLACE',
        'MERGE',
        'CREATE',
        'ALTER',
        'DROP',
        'RENAME',
        'GRANT',
        'REVOKE',
        'SET',
        'EXECUTE',
        'BEGIN',
        'COMMIT',
        'ROLLBACK',
        'CALL',
      ]

      for (const kw of blockedKeywords) {
        if (this.hasForbiddenToken(withoutTrailingSemicolons, kw)) {
          return `Comando não permitido: ${kw}.`
        }
      }

      const blockedMeta = [
        'information_schema',
        'pg_catalog',
        'pg_tables',
        'pg_attribute',
      ]

      for (const t of blockedMeta) {
        if (this.hasForbiddenToken(withoutTrailingSemicolons, t)) {
          return `Acesso a metadados não permitido: ${t}.`
        }
      }

      return ''
    },

    /**
     * Executa a consulta SQL informada e atualiza o resultado da tela.
     */
    async executar() {
      try {
        if (this.sqlBlockedReason) {
          this.error = this.sqlBlockedReason
          return
        }

        this.error = ''
        this.loading = true
        this.resultado = null

        if (!this.sqlEditor?.trim()) {
          this.error = 'SQL é obrigatório.'
          return
        }

        const payload = {
          activeKey: this.activeKey,
          params: this.params,
          sql: this.sqlEditor,
          ibge: this.$store.state.cod_ibge,
        }
        const resp = await this.$api.post('/app/cidadao/sql', payload)
        if (resp.data.error) {
          this.error = resp.data.message
          return
        }
        this.resultado = resp.data?.dados ?? []
      } catch (e) {
        console.log(e)
        this.error =
          e?.response?.data?.message ||
          e?.message ||
          'Erro ao executar a query.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.table-results {
  max-height: 350px;
  overflow: auto;
}
</style>
